import React, { ReactChildren, useContext } from "react";
import { useRouter } from "next/router";
// import FadeIn from "react-lazyload-fadein";
import classnames from "classnames";

// Logos
import PlaystoreBadge from "@vahak/core/dist/icons/PlaystoreBadge.svg";

// Components
import MostSearchedLinks from "./MostSearchedLinks/MostSearchedLinks";
import SocialLinks from "./SocialLinks/SocialLinks";
import AppLink from "components/utils/app-link/AppLink";

// Utils & helper functions
import A from "@vahak/core/dist/components/app-anchor/A";
import { AppContext } from "@vahak/core/dist/app-context";
import { FOOTER_ITEM_ATTRIBUTE, FooterItemsConstant } from "@vahak/core/dist/constants/misc";
import { GA4EventNames, GA4AppClickSource, GA4ContactUsClickSource } from "@vahak/core/dist/constants/event-names";
import { FooterItemsGaEvents } from "@vahak/core/dist/constants/eventsMapObj";

import VahakLogoWhite from "@vahak/core/dist/icons/VahakLogoWhite_v03.svg";

// Style
import footerStyles from "./Footer.module.scss";

import { ROUTES } from "@vahak/core/dist/constants/routeStrings";
import { PLATFORM_STATS } from "@vahak/core/dist/constants/platformStats";
import { MARKETPLACE_ROUTE_CONFIG } from "../../../constants/marketplace.constants";
import { ga4sendEvent, moEngageUserEventTrack } from "@vahak/core/dist/_services/user-analytics.service";
import { getLcvRouteFooterLinkGroups } from "../../lcv-route-landing/lcvRouteFooterLinks";
import { getCityPageFooterLinkGroups } from "../../city/cityFooterLinks";

const HighlightedLink = ({ path, children }: { path: string; children: ReactChildren | string }) => {
    return (
        <>
            &nbsp;
            <AppLink href={path} className={footerStyles.link__highlighted}>
                {children}
            </AppLink>
            &nbsp;
        </>
    );
};
const Footer = () => {
    const { isMobile, userId, name, serviceType, companyId } = useContext(AppContext);
    const router = useRouter();

    const onClickSendGaEvents = (e: React.MouseEvent<HTMLLIElement | HTMLDivElement, MouseEvent>) => {
        const attribute = e.currentTarget.getAttribute(FOOTER_ITEM_ATTRIBUTE);

        if (attribute) {
            let val = FooterItemsGaEvents[attribute];
            if (val !== undefined) {
                ga4sendEvent("website_bottom_navigation_web", { value: val });
            }
            if (attribute === FooterItemsConstant.DOWNLOAD_OUR_APP) {
                ga4sendEvent(GA4EventNames.Download_app_clicked_web, {
                    company_id: companyId,
                    user_name: name,
                    service_type: serviceType,
                    source_screen: GA4AppClickSource.homepage_bottom_screen
                });
            } else if (attribute === FooterItemsConstant.CONTACT_US) {
                ga4sendEvent(GA4EventNames.Contact_us_clicked_web, {
                    company_id: companyId,
                    user_name: name,
                    service_type: serviceType,
                    section: GA4ContactUsClickSource.homepage_bottom_section
                });
            }
        }
    };

    return (
        <>
            <footer className={classnames(footerStyles.footer)}>
                <div className="container">
                    <div className={classnames("row", footerStyles.row, footerStyles.topLinks)}>
                        <div className={classnames(footerStyles.col, "col-12", "col-lg-4")}>
                            <div className={classnames(footerStyles.logoFooter)}>
                                <div
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                        if (companyId) {
                                            if (router?.pathname?.includes("/dashboard")) return;
                                            router.push(ROUTES.dashboard.getUrl());
                                        } else {
                                            if (router?.pathname?.includes("/marketplace")) {
                                                router.push("/");
                                                return;
                                            }
                                            router.push(MARKETPLACE_ROUTE_CONFIG.loadUrl);
                                        }
                                    }}
                                    title="Vahak - Book LCVs, containers and trucks from trusted transporters across India"
                                >
                                    <VahakLogoWhite width="140" />
                                </div>
                            </div>

                            <p>Book LCVs, containers and trucks in minutes from trusted transporters across India.</p>
                        </div>
                        {isMobile && (
                            <div className={classnames(footerStyles.col, "col-12")}>
                                <h4 className={classnames(footerStyles.footerHead)}>Connect with us on</h4>
                                <SocialLinks />
                            </div>
                        )}
                        <div className={classnames(footerStyles.col, "col-6", "col-md-4", "col-lg-3")}>
                            <h4 className={classnames(footerStyles.footerHead)}>Company</h4>
                            <ul className={classnames(footerStyles.footerList)}>
                                <li
                                    {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.ABOUT_US }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink href="/about-us" title="Feel free to contact Vahak team for any queries">
                                        About us
                                    </AppLink>
                                </li>
                                <li
                                    {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.CONTACT_US }}
                                    onClick={() => {
                                        onClickSendGaEvents;
                                        moEngageUserEventTrack("contact_us_clicked_web", {
                                            user_id: userId,
                                            user_name: name,
                                            service_type: serviceType,
                                            section: "Homepage bottom section"
                                        });
                                    }}
                                >
                                    <AppLink href="/contact-us" title="Feel free to contact Vahak team for any queries">
                                        Contact Us
                                    </AppLink>
                                </li>
                                <li
                                    {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.CAREERS }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink href="/careers" title="Click to know more about open positions in Vahak">
                                        Careers
                                        {/* <span className={classnames(footerStyles.hiring)}>We’re hiring</span> */}
                                    </AppLink>
                                </li>
                            </ul>

                            <div className={classnames("d-block", "d-md-none", "mt-5")}>
                                <h4 className={classnames(footerStyles.footerHead)}>Transport Nagar</h4>
                                <ul className={classnames(footerStyles.footerList)}>
                                    <li
                                        {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.FAQ }}
                                        onClick={onClickSendGaEvents}
                                    >
                                        <AppLink
                                            href={`/transport-nagar/sanjay-gandhi`}
                                            title="Sanjay gandhi transport nagar"
                                        >
                                            Sanjay Gandhi
                                        </AppLink>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div className={classnames(footerStyles.col, "col-6", "col-md-4", "col-lg-2")}>
                            <h4 className={classnames(footerStyles.footerHead)}>Misc</h4>
                            <ul className={classnames(footerStyles.footerList)}>
                                <li
                                    {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.FAQ }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href={`/frequently-asked-questions`}
                                        title="Click to know more about FAQ’s"
                                    >
                                        Frequently Asked Questions
                                    </AppLink>
                                </li>
                                <li
                                    {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.TERMS_AND_CONDITION }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href={`/terms-and-conditions`}
                                        title="Click to read our Terms and Conditions"
                                        locale=""
                                    >
                                        Terms and Conditions
                                    </AppLink>
                                </li>
                                <li
                                    {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.PAYMENT_GUIDELINES }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href={`/payments-terms-and-conditions`}
                                        title="Click to know more about Payment Guidelines"
                                        locale=""
                                    >
                                        Payment Guidelines and T&C
                                    </AppLink>
                                </li>
                                <li
                                    {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.CASHBACK_TERMS_AND_CONDITIONS }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href="/cashback-terms-and-condition"
                                        title="Click to know more about Cashback Guidelines"
                                        locale=""
                                    >
                                        Cashback T&C
                                    </AppLink>
                                </li>
                                <li
                                    {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.MEDIBUDDY_TERMS_AND_CONDITIONS }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href="/medibuddy-terms-and-conditions"
                                        title="Click to know more about Medibuddy Terms and Conditions"
                                        locale=""
                                    >
                                        Medibuddy T&C
                                    </AppLink>
                                </li>
                                <li
                                    {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.TAP_N_GO_TERMS_AND_CONDITIONS }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href="/tapngo-terms-and-conditions"
                                        title="Click to know more about Tap N Go Terms and Conditions"
                                        locale=""
                                    >
                                        Assured Loads T&C
                                    </AppLink>
                                </li>
                                {/* <li
                                    {...{
                                        [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.BUSINESS_LOANS_TERMS_AND_CONDITIONS
                                    }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href="/business-loans-terms-and-conditions"
                                        title="Click to know more about  Business Loans Terms and Conditions"
                                        locale=""
                                    >
                                        Business Loans T&C
                                    </AppLink>
                                </li> */}
                                <li
                                    {...{
                                        [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.CI_TERMS_AND_CONDITIONS
                                    }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href={`/${FooterItemsConstant.CI_TERMS_AND_CONDITIONS}`}
                                        title="Free Cargo Insurance Terms and Conditions"
                                        locale=""
                                    >
                                        Free Cargo Insurance T&C
                                    </AppLink>
                                </li>
                                <li
                                    {...{
                                        [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.GPS_TERMS_AND_CONDITIONS
                                    }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href={`/${FooterItemsConstant.GPS_TERMS_AND_CONDITIONS}`}
                                        title="GPS Terms and Conditions"
                                        locale=""
                                    >
                                        GPS T&C
                                    </AppLink>
                                </li>
                                <li
                                    {...{
                                        [FOOTER_ITEM_ATTRIBUTE]: "refund-and-cancellation-terms-and-conditions"
                                    }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href="/refund-and-cancellation-terms-and-conditions"
                                        title="Refund and Cancellation Terms and Conditions"
                                        locale=""
                                    >
                                        Refund and Cancellation T&C
                                    </AppLink>
                                </li>
                                <li
                                    {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.PRIVACY_POLICY }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href={`/privacy-policy`}
                                        title="Click to read our Privacy Policy"
                                        locale=""
                                    >
                                        Privacy Policy
                                    </AppLink>
                                </li>
                            </ul>
                        </div>
                        <div
                            className={classnames(
                                footerStyles.col,
                                "col-6",
                                "col-md-4",
                                "col-lg-2",
                                "d-none",
                                "d-md-block"
                            )}
                        >
                            <h4 className={classnames(footerStyles.footerHead)}>Transport Nagar</h4>
                            <ul className={classnames(footerStyles.footerList)}>
                                <li
                                    {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.FAQ }}
                                    onClick={onClickSendGaEvents}
                                >
                                    <AppLink
                                        href={`/transport-nagar/sanjay-gandhi`}
                                        title="Sanjay gandhi transport nagar"
                                    >
                                        Sanjay Gandhi
                                    </AppLink>
                                </li>
                            </ul>
                        </div>
                        {/* Temporarily hidden */}
                        <div className={classnames(footerStyles.col, "d-none", "col-6", "col-md-4", "col-lg-2")}>
                            <h4 className={classnames(footerStyles.footerHead)}>Products</h4>
                            <ul className={classnames(footerStyles.footerList)}>
                                <li>
                                    <AppLink
                                        href={`${ROUTES.marketplace.getUrl("load")}`}
                                        title="Click to know more about live marketplace"
                                    >
                                        Live Marketplace
                                    </AppLink>
                                </li>
                                <li>
                                    <AppLink
                                        href={`/transporters`}
                                        title="Click to know more about Transport Directory"
                                    >
                                        Transport Directory
                                    </AppLink>
                                </li>
                            </ul>
                        </div>
                        {/* Resources / Legal columns removed — blogs & glossary deprecated (301 → home) */}
                        {/* <div className={classnames(footerStyles.col, "d-none", "col-6", "col-md-4", "col-lg-2")}>
                            <h4 className={classnames(footerStyles.footerHead)}>Legal</h4>
                            <ul className={classnames(footerStyles.footerList)}>
                                <li>
                                    <Link href={`/privacy-policy`}>
                                        <a title="Click to know more about privacy policy">Privacy Policy</a>
                                    </Link>
                                </li>
                                <li>
                                    <Link href={`/terms-and-conditions`}>
                                        <a title="Click to know more about Terms & Conditions">Terms & Conditions</a>
                                    </Link>
                                </li>
                            </ul>
                        </div> */}
                    </div>
                    <div className={classnames("row", footerStyles.row, footerStyles.getInTouch)}>
                        {!isMobile && (
                            <div className={classnames(footerStyles.col, "col-12", "col-lg-4")}>
                                <h4 className={classnames(footerStyles.footerHead)}>Connect with us on</h4>
                                <SocialLinks />
                            </div>
                        )}
                        <div className={classnames(footerStyles.col, "col-lg-3")}>
                            <h4 className={classnames(footerStyles.footerHead)}>Download our app on</h4>
                            <div
                                {...{ [FOOTER_ITEM_ATTRIBUTE]: FooterItemsConstant.DOWNLOAD_OUR_APP }}
                                onClick={() => {
                                    onClickSendGaEvents;
                                    moEngageUserEventTrack("download_app_clicked_web", {
                                        user_id: userId,
                                        user_name: name,
                                        service_type: serviceType,
                                        source_section: "Footer section"
                                    });
                                }}
                            >
                                <A
                                    openInNewTab
                                    title="Book Trucks for all India, Download Vahak"
                                    href="https://6r7cz.app.link/googleplay"
                                >
                                    <PlaystoreBadge
                                        className={classnames(footerStyles.footerPlaystoreIcon, "img-fluid")}
                                        width="165"
                                        height="50"
                                        title="Book Trucks for all India, Download Vahak"
                                    />
                                </A>
                            </div>
                        </div>
                    </div>
                    {/* Divider */}
                    <div className={classnames(footerStyles.divider)} />
                    <div className={classnames("row", footerStyles.row, footerStyles.mostSearchedLinkContainer)}>
                        <div className={classnames(footerStyles.col, "col")}>
                            <MostSearchedLinks />
                        </div>
                    </div>
                    {/* Divider */}
                    <div className={classnames(footerStyles.divider)} />
                    <div className={classnames("row", footerStyles.row)}>
                        <div className={classnames("col", footerStyles.col)}>
                            <div className={classnames(footerStyles.trustContent)}>
                                <p>
                                    Find LCVs faster on Vahak, India&apos;s leading online lorry booking and transport
                                    marketplace. Connect with verified transporters and lorry owners across Pan India
                                    and find the right vehicle for your load. Get access to competitive freight rates
                                    and choose from a wide network of LCVs, mini trucks, Tata Ace, Bolero, Bada Dost and
                                    other commercial vehicles. Post your load and get matched with verified transporters
                                    for faster load booking and confirmation. Find vehicles for local, intercity and
                                    long-distance transportation across major routes in India. With one of India&apos;s
                                    largest networks of {PLATFORM_STATS.transporters.lakhs} transporters and lorry
                                    owners, Vahak makes finding and booking an LCV simpler and faster.
                                </p>
                                <p className={footerStyles.seoContent}>
                                    {FOOTER_SECTIONS.map(({ title, items, routeGroups }) => (
                                        <React.Fragment key={title}>
                                            <h2>{title}</h2>
                                            <div className={footerStyles.grid}>
                                                {routeGroups
                                                    ? routeGroups.map((group, groupIndex) => {
                                                          const alternate =
                                                              group.alternates.find(
                                                                  (entry) => entry.locale === router.locale
                                                              ) ??
                                                              group.alternates.find((entry) => entry.locale === "en");

                                                          if (!alternate) {
                                                              return null;
                                                          }

                                                          return (
                                                              <React.Fragment key={group.slug}>
                                                                  <HighlightedLink path={alternate.path}>
                                                                      {alternate.label}
                                                                  </HighlightedLink>
                                                                  {groupIndex < routeGroups.length - 1 && " | "}
                                                              </React.Fragment>
                                                          );
                                                      })
                                                    : items.map(({ path, label }, ix) => (
                                                          <React.Fragment key={path}>
                                                              <HighlightedLink path={path}>{label}</HighlightedLink>
                                                              {ix < items.length - 1 && " | "}
                                                          </React.Fragment>
                                                      ))}
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
            <footer className={classnames(footerStyles.footer, footerStyles.footerBar)}>
                <div className="container">
                    {/* Divider */}
                    <div className={classnames(footerStyles.divider)} />
                    <div className={classnames(footerStyles.row, "row")}>
                        <div className={classnames(footerStyles.col, "col-12", "order-1", "col-sm-5")}>
                            <div className={classnames(footerStyles.copyRights)}>
                                <p>
                                    © {new Date().getFullYear()} Epictus Solutions India Private Limited. All Rights
                                    Reserved
                                </p>
                            </div>
                        </div>
                        <div className={classnames(footerStyles.col, "col-12", "order-3", "col-sm-2", "order-sm-2")}>
                            <div className={classnames(footerStyles.madeInIndia)}>
                                <span>
                                    Made with{" "}
                                    <span role="img" aria-label="love">
                                        ❤️
                                    </span>{" "}
                                    in India
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default Footer;

const POPULAR_LOAD_CITIES = [
    { slug: "mumbai-maharashtra-india", label: "Mumbai" },
    { slug: "new-delhi-delhi-india", label: "Delhi" },
    { slug: "bengaluru-karnataka-india", label: "Bangalore" },
    { slug: "chennai-tamil-nadu-india", label: "Chennai" },
    { slug: "hyderabad-telangana-india", label: "Hyderabad" },
    { slug: "pune-maharashtra-india", label: "Pune" },
    { slug: "ahmedabad-gujarat-india", label: "Ahmedabad" },
    { slug: "kolkata-west-bengal-india", label: "Kolkata" }
];

const TOP_CITIES = POPULAR_LOAD_CITIES;

const VAHAK_SERVICES = [
    { path: "/full-truck-load", label: "Full Truck Load (FTL)" },
    { path: "/part-load-booking", label: "Part Load Booking" },
    { path: "/freight-calculator", label: "Freight Rate Calculator" },
    { path: "/transporters", label: "Find Transporters" },
    { path: "/online-truck-booking", label: "Online Truck Booking" }
];

const LCV_ROUTE_FOOTER_LINK_GROUPS = getLcvRouteFooterLinkGroups();
const CITY_PAGE_FOOTER_LINK_GROUPS = getCityPageFooterLinkGroups();

const FOOTER_SECTIONS = [
    {
        title: "LCV booking routes",
        routeGroups: LCV_ROUTE_FOOTER_LINK_GROUPS
    },
    {
        title: "Book LCV by city",
        routeGroups: CITY_PAGE_FOOTER_LINK_GROUPS
    },
    // {
    //     title: "Popular truck booking routes",
    //     items: POPULAR_ROUTES
    // },
    {
        title: "Search loads in top cities",
        items: TOP_CITIES.map(({ slug, label }) => ({
            path: `/marketplace/load/${slug}`,
            label: `Loads in ${label}`
        }))
    },
    {
        title: "Vahak services",
        items: VAHAK_SERVICES
    }
];
